import React, { Suspense, useEffect, useRef, useState } from "react";
import { useGlobalState } from "../../GlobalStates";
import Button from "@mui/material/Button";
import { useLocation } from "react-router-dom";
import { useUserLocale } from "../../customHooks/useUserLocale";
import { countryWithLandingPage } from "../../utils/constants";
import { useIsMobile } from "../../hooks/responsive";
import { getWelcomePromoEligibility } from "../../services/user";
import { getActiveLuckyDrawCampaign } from "../../services/affiliate";
import { getSkiCopy } from "../../game/ski/skiTranslations";
import { useTabBarOverlay } from "../layout/appTabBar";

const SkiGameModal = React.lazy(() => import("../../game/ski/SkiGameModal"));

const isWelcomeGiftPath = (pathname) => {
  const normalizedPathname = pathname.toLowerCase();
  return (
    normalizedPathname === "/" ||
    normalizedPathname.startsWith("/all-trips") ||
    normalizedPathname === "/sofest" ||
    normalizedPathname.startsWith("/sofest/") ||
    normalizedPathname.startsWith("/trip/") ||
    normalizedPathname.startsWith("/trips/") ||
    countryWithLandingPage.some(
      (page) => normalizedPathname === `/${page}` || normalizedPathname.startsWith(`/${page}/`)
    )
  );
};

export const LoginWidget = () => {
  const location = useLocation();
  const isMobile = useIsMobile();
  const tabBarOverlay = useTabBarOverlay();
  const { community, language, loading: localeLoading } = useUserLocale();
  const skiCopy = getSkiCopy(language);

  const [user] = useGlobalState("user");
  const [userLoading] = useGlobalState("userLoading");
  const [openLogin, setOpenLogin] = useGlobalState("openLogin");
  const [gameOpen, setGameOpen] = useState(false);
  const [activeCampaign, setActiveCampaign] = useGlobalState("activeLuckyDrawCampaign");
  const [openLuckyDrawGame, setOpenLuckyDrawGame] = useGlobalState("openLuckyDrawGame");
  const autoOpenedLocationRef = useRef(null);
  const isLoggedIn = Boolean(user?.id || localStorage.getItem("JWT_TOKEN"));

  // Shared with LoginModal so signed-in promo content cannot render while this
  // backend eligibility check is pending or after it fails.
  const [promoEligible, setPromoEligible] = useGlobalState("welcomePromoEligible");

  const supportedCommunity = ["sg", "tw", "hk", "my"].includes(community);

  const welcomeGiftOpenedRef = useRef(false);

  useEffect(() => {
    // Warm the game chunk before the floating crystal is clicked so opening
    // the experience does not wait for the modal code and artwork imports.
    import("../../game/ski/SkiGameModal");
  }, []);

  useEffect(() => {
    // Reward and trip navigation unmount the visible game surface, but this
    // component can remain mounted. Clear its state so the modal cannot reopen
    // when the destination route also supports the floating campaign widget.
    setGameOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!openLuckyDrawGame || !activeCampaign) return;
    setGameOpen(true);
    setOpenLuckyDrawGame(false);
  }, [activeCampaign, openLuckyDrawGame, setOpenLuckyDrawGame]);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const shouldAutoOpen = searchParams.get("ski_run") === "true";
    const locationKey = location.key || `${location.pathname}${location.search}`;

    if (
      !shouldAutoOpen ||
      !activeCampaign ||
      localeLoading ||
      userLoading ||
      !supportedCommunity ||
      autoOpenedLocationRef.current === locationKey
    ) {
      return;
    }

    autoOpenedLocationRef.current = locationKey;
    setGameOpen(true);
  }, [
    activeCampaign,
    localeLoading,
    location.key,
    location.pathname,
    location.search,
    supportedCommunity,
    userLoading,
  ]);

  useEffect(() => {
    let cancelled = false;
    getActiveLuckyDrawCampaign()
      .then(({ campaign }) => {
        if (!cancelled) setActiveCampaign(campaign || null);
      })
      .catch(() => {
        if (!cancelled) setActiveCampaign(null);
      });
    return () => {
      cancelled = true;
    };
  }, [setActiveCampaign]);

  useEffect(() => {
    if (!isLoggedIn || !user?.id || !supportedCommunity) {
      setPromoEligible(null);
      return;
    }
    setPromoEligible(null);
    let cancelled = false;
    getWelcomePromoEligibility()
      .then((res) => {
        if (!cancelled) {
          setPromoEligible(Boolean(res?.data?.eligible));
        }
      })
      .catch(() => {
        // Background check — on failure just keep the surfaces hidden.
        if (!cancelled) {
          setPromoEligible(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [isLoggedIn, user?.id, supportedCommunity, setPromoEligible]);

  useEffect(() => {
    if (isLoggedIn && promoEligible === false) {
      setOpenLogin(false);
    }
  }, [isLoggedIn, promoEligible, setOpenLogin]);

  // Give eligible logged-out visitors a chance to discover the welcome gift.
  // Once it has opened, automatically or manually, only the launcher may open
  // it again during this visit.
  useEffect(() => {
    if (
      isLoggedIn ||
      activeCampaign !== null ||
      localeLoading ||
      userLoading ||
      !supportedCommunity ||
      !isWelcomeGiftPath(location.pathname) ||
      welcomeGiftOpenedRef.current ||
      openLogin
    ) {
      return undefined;
    }

    const timer = setTimeout(() => {
      if (!welcomeGiftOpenedRef.current && !isLoggedIn) {
        welcomeGiftOpenedRef.current = true;
        setOpenLogin(true);
      }
    }, 10000);

    return () => clearTimeout(timer);
  }, [
    activeCampaign,
    isLoggedIn,
    localeLoading,
    location.pathname,
    openLogin,
    setOpenLogin,
    supportedCommunity,
    userLoading,
  ]);

  const openWinterExperience = () => {
    setGameOpen(true);
  };

  const shouldShowWelcomeGift = () => {
    if (
      location.pathname.toLowerCase() !== "/c/aurora" &&
      isWelcomeGiftPath(location.pathname) &&
      !localeLoading &&
      supportedCommunity &&
      !userLoading
    ) {
      return true;
    }
  };

  if (shouldShowWelcomeGift() && activeCampaign !== undefined) {
    const isTaiwan = community === "tw";
    const showWelcomeGift = !isLoggedIn && !activeCampaign;
    const normalizedLanguage = String(language || "").toLowerCase();
    const campaignNameKey = normalizedLanguage.startsWith("zh-hans")
      ? "display_name_zh_hans"
      : normalizedLanguage.startsWith("zh-hant")
        ? "display_name_zh_hant"
        : "display_name_en";
    const campaignName = activeCampaign?.[campaignNameKey] || skiCopy.gameName;

    return (
      <>
        {activeCampaign && (
          <Button
            onClick={openWinterExperience}
            aria-label={`Play ${campaignName}`}
            style={{
              height: "3.5rem",
              width: "3.5rem",
              minWidth: "3.5rem",
              padding: 0,
              borderRadius: "50%",
              zIndex: 1001,
              position: "fixed",
              // In the app, clear its floating tab bar, the promo banner and
              // the active-trip card that sit at the bottom.
              bottom: tabBarOverlay
                ? "calc(7rem + var(--app-tabbar-inset, 0px) + var(--app-bottom-banner, 0px))"
                : "6rem",
              right: isMobile ? "1rem" : "3rem",
              color: "white",
            }}
          >
            <img
              className="WinterCrystalLauncher"
              src={activeCampaign.icon_url || "/new_logo/winter_crystal.png"}
              alt=""
              onError={(event) => {
                event.currentTarget.onerror = null;
                event.currentTarget.src = "/new_logo/winter_crystal.png";
              }}
            />
          </Button>
        )}
        {showWelcomeGift && (
          <Button
            onClick={() => {
              welcomeGiftOpenedRef.current = true;
              setOpenLogin(true);
            }}
            id="welcome-gift-launcher"
            aria-label="Open welcome gift"
            style={{
              height: "3.5rem",
              width: "3.5rem",
              minWidth: "3.5rem",
              padding: 0,
              borderRadius: "50%",
              zIndex: 1000,
              position: "fixed",
              bottom: isTaiwan ? "10rem" : "6rem",
              right: isMobile ? "1rem" : "3rem",
              color: "white",
            }}
          >
            <img
              src="/images/so_gift_close.png"
              alt=""
              style={{ width: "100%", height: "100%", objectFit: "contain" }}
            />
          </Button>
        )}
        {gameOpen && (
          <Suspense
            fallback={
              <div
                role="dialog"
                aria-label={skiCopy.loadingPrize}
                style={{
                  position: "fixed",
                  inset: 0,
                  zIndex: 2147483000,
                  display: "grid",
                  placeItems: "center",
                  background: "rgba(6, 19, 38, 0.9)",
                  color: "white",
                  fontFamily: "monospace",
                  fontWeight: 700,
                }}
              >
                {skiCopy.loadingPrize}
              </div>
            }
          >
            <SkiGameModal
              onClose={() => setGameOpen(false)}
              campaignId={activeCampaign?.id}
              campaignName={campaignName}
              isLoggedIn={isLoggedIn}
            />
          </Suspense>
        )}
      </>
    );
  }

  return null;
};
